import { useState, useRef, useEffect } from "react"
import { ChevronDown } from "lucide-react"
import { inputClass } from "@/components/ui/input"
import { colors, accent } from '@/lib/tokens'

const ROLES = [
  { value: "student", label: "Student" },
  { value: "developer", label: "Developer" },
  { value: "founder", label: "Founder" },
  { value: "other", label: "Other" },
]

export function RoleDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setDropdownOpen(false)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  const selected = ROLES.find((r) => r.value === value)

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setDropdownOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={dropdownOpen}
        className={`h-11 flex items-center justify-between text-left cursor-pointer focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)] ${inputClass}`}
      >
        <span className={selected ? "text-[#0a0a0a]" : "text-[#a8a29e]"}>
          {selected ? selected.label : "Select your role"}
        </span>
        <ChevronDown
          className="h-4 w-4 text-[#a8a29e] transition-transform duration-150"
          style={{ transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>

      {dropdownOpen && (
        <div
          role="listbox"
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-[#e7e5e4] bg-white shadow-lg"
        >
          {ROLES.map((r) => (
            <button
              key={r.value}
              type="button"
              role="option"
              aria-selected={value === r.value}
              onClick={() => {
                onChange(r.value)
                setDropdownOpen(false)
              }}
              className="flex w-full items-center justify-between px-3 py-2.5 text-sm text-left transition-colors hover:bg-[#f5f5f4] focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)]"
              style={{ color: value === r.value ? accent : colors.textSecondary }}
            >
              <span>{r.label}</span>
              {value === r.value && <span className="text-[#F26B1D] text-xs font-medium">Selected</span>}
            </button>
          ))}
        </div>
      )}

      {/* hidden input so FormData picks up the value */}
      <input type="hidden" name="role" value={value} />
    </div>
  )
}
